import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Edit, Trash, ArrowUp, ArrowDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { secureLog } from '@/lib/security';

interface Phase {
  id: string;
  section_id: string;
  phase_title: string;
  display_order: number;
}

interface AdminPhaseManagerProps {
  sectionId: string;
  sectionName: string;
}

export const AdminPhaseManager = ({ sectionId, sectionName }: AdminPhaseManagerProps) => {
  const [phases, setPhases] = useState<Phase[]>([]);
  const [editingPhase, setEditingPhase] = useState<Phase | null>(null);
  const [newPhaseTitle, setNewPhaseTitle] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const loadPhases = async () => {
    try {
      const { data, error } = await supabase
        .from('template_phases')
        .select('*')
        .eq('template_id', sectionId)
        .order('display_order');
      
      if (error) throw error;
      setPhases((data as any[]) || []);
    } catch (error) {
      secureLog.error('Error loading phases:', error);
      toast({
        title: "Error",
        description: "Failed to load phases",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPhases();
  }, [sectionId]);

  const createPhase = async () => {
    if (!newPhaseTitle.trim()) return;
    
    try {
      const maxOrder = Math.max(...phases.map(p => p.display_order), -1);
      const { error } = await supabase
        .from('template_phases')
        .insert({
          template_id: sectionId,
          phase_title: newPhaseTitle.trim(),
          display_order: maxOrder + 1
        });
      
      if (error) throw error;
      
      setNewPhaseTitle('');
      loadPhases();
      toast({
        title: "Success",
        description: "Phase created successfully"
      });
    } catch (error) {
      secureLog.error('Error creating phase:', error);
      toast({
        title: "Error",
        description: "Failed to create phase",
        variant: "destructive"
      });
    }
  };

  const updatePhase = async (phase: Phase) => {
    try {
      const { error } = await supabase
        .from('template_phases')
        .update({
          phase_title: phase.phase_title
        })
        .eq('id', phase.id);
      
      if (error) throw error;
      
      setEditingPhase(null);
      loadPhases();
      toast({
        title: "Success",
        description: "Phase updated successfully"
      });
    } catch (error) {
      secureLog.error('Error updating phase:', error);
      toast({
        title: "Error",
        description: "Failed to update phase",
        variant: "destructive"
      });
    }
  };

  const deletePhase = async (phaseId: string) => {
    if (!confirm('Are you sure? This will delete all items in this phase.')) return;
    
    try {
      const { error } = await supabase
        .from('template_phases')
        .delete()
        .eq('id', phaseId);
      
      if (error) throw error;
      
      loadPhases();
      toast({
        title: "Success",
        description: "Phase deleted successfully"
      });
    } catch (error) {
      secureLog.error('Error deleting phase:', error);
      toast({
        title: "Error",
        description: "Failed to delete phase",
        variant: "destructive"
      });
    }
  };

  const movePhase = async (phaseId: string, direction: 'up' | 'down') => {
    const currentIndex = phases.findIndex(p => p.id === phaseId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= phases.length) return;
    
    const updatedPhases = [...phases];
    [updatedPhases[currentIndex], updatedPhases[newIndex]] = [updatedPhases[newIndex], updatedPhases[currentIndex]];
    
    try {
      const updates = updatedPhases.map((phase, index) => ({
        id: phase.id,
        display_order: index
      }));
      
      for (const update of updates) {
        await supabase
          .from('template_phases')
          .update({ display_order: update.display_order })
          .eq('id', update.id);
      }
      
      loadPhases();
      toast({
        title: "Success",
        description: "Phase order updated"
      });
    } catch (error) {
      secureLog.error('Error reordering phases:', error);
      toast({
        title: "Error",
        description: "Failed to reorder phases",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading phases...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Phases in {sectionName}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            placeholder="New phase title..."
            value={newPhaseTitle}
            onChange={(e) => setNewPhaseTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && createPhase()}
          />
          <Button onClick={createPhase} disabled={!newPhaseTitle.trim()} variant="accent">
            <Plus className="h-4 w-4 mr-2" />
            Add Phase
          </Button>
        </div>

        <div className="space-y-2">
          {phases.map((phase, index) => (
            <Card key={phase.id} className="p-3">
              {editingPhase?.id === phase.id ? (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor={`title-${phase.id}`}>Phase Title</Label>
                    <Input
                      id={`title-${phase.id}`}
                      value={editingPhase.phase_title}
                      onChange={(e) => setEditingPhase({
                        ...editingPhase,
                        phase_title: e.target.value
                      })}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => updatePhase(editingPhase)} variant="accent">
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={() => setEditingPhase(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-normal">{phase.phase_title}</h5>
                    <p className="text-sm text-muted-foreground">Order: {phase.display_order}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => movePhase(phase.id, 'up')}
                      disabled={index === 0}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => movePhase(phase.id, 'down')}
                      disabled={index === phases.length - 1}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingPhase(phase)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deletePhase(phase.id)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};